import { Button, Classes, Dialog, FormGroup, Icon } from '@blueprintjs/core'
import { lang } from 'botpress/shared'
import React from 'react'
import '../../../user/modern-settings.css'

export interface ChannelSelectionDialogProps {
  isOpen: boolean
  closeChannelDialog: () => void
  selectedChannel: any
  setSelectedChannel: (channel: any) => void
  resetValues: () => void
  savedSubData: any
  channelList: any[]
  botId: string
  verifyToken: string
  botToken: string
  setBotToken: (value: string) => void
  slackBotToken: string
  setSlackBotToken: (value: string) => void
  slackSigningSecret: string
  setSlackSigningSecret: (value: string) => void
  messengerAccessToken: string
  setMessengerAccessToken: (value: string) => void
  messengerAppSecret: string
  setMessengerAppSecret: (value: string) => void
  twilioAccountSid: string
  setTwilioAccountSid: (value: string) => void
  twilioAuthToken: string
  setTwilioAuthToken: (value: string) => void
  isProcessing: boolean
  createBot: (e: React.FormEvent) => void
  error: any
}

const ChannelSelectionDialog: React.FC<ChannelSelectionDialogProps> = ({
  isOpen,
  closeChannelDialog,
  selectedChannel,
  setSelectedChannel,
  resetValues,
  savedSubData,
  channelList,
  botId,
  verifyToken,
  botToken,
  setBotToken,
  slackBotToken,
  setSlackBotToken,
  slackSigningSecret,
  setSlackSigningSecret,
  messengerAccessToken,
  setMessengerAccessToken,
  messengerAppSecret,
  setMessengerAppSecret,
  twilioAccountSid,
  setTwilioAccountSid,
  twilioAuthToken,
  setTwilioAuthToken,
  isProcessing,
  createBot,
  error,
}) => {

  const baseUrl = window.location.origin
  const channelInstructions = {
    telegram: `
          <ol style="margin: 0; padding-left: 20px; font-size: 13px; color: #475569; line-height: 1.6;">
            <li>Open <strong><a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" style="color: #028ec0; font-weight: 600;">@BotFather</a></strong> in Telegram.</li>
            <li>Type <code>/newbot</code> and follow the steps.</li>
            <li>Copy the <strong>botToken</strong> and provide it below.</li>
          </ol>
        `,
    slack: `
          <ol style="margin: 0; padding-left: 20px; font-size: 13px; color: #475569; line-height: 1.6;">
            <li>Navigate to your <b>Slack Apps</b> page.</li>
            <li>Click <b>Create New App</b>, choose <b>From scratch</b>, and name it.</li>
            <li>Go to the <b>Features</b> section, select <b>Interactivity & Shortcuts</b>, and toggle it to <b>On</b>.</li>
            <li>Set the Request URL to: <code style="word-break: break-all;">${baseUrl}/api/v1/messaging/webhooks/${botId}/slack/interactive</code></li>
            <li>Click <b>Save Changes.</b></li>
            <li>In <b>Features</b>, open <b>OAuth & Permissions</b> and add scope: <code>chat:write</code></li>
            <li>Under <b>App Home</b>, enable <b>Allow users to send Slash commands and messages from the messages tab.</b></li>
            <li>Head over to <b>Install App</b>, click <b>Install to Workspace</b>, then click Allow.</li>
            <li>Copy the <b>Bot Token</b> and paste it below.</li>
            <li>Locate the <b>Signing Secret</b> in <b>Basic Information</b>, and paste it below.</li>
            <li>Click <b>Submit</b> button here and wait for bot creation.</li>
            <li>Go back to Slack app page, navigate to <b>Event Subscriptions</b>, and enable it.</li>
            <li>Set the Request URL to: <code style="word-break: break-all;">${baseUrl}/api/v1/messaging/webhooks/${botId}/slack/events</code></li>
            <li>Under <b>Subscribe to Bot Events</b>, add: <code>message.im</code> and <code>message.channels</code></li>
            <li>Wait for the <b>Verified</b> confirmation and save your changes.</li>
            <li>Click <b>Reinstall app</b> at the top banner and restart Slack.</li>
          </ol>
        `,
    webchat: `
          <div style="font-size: 13px; color: #475569; line-height: 1.5; margin-bottom: 12px;">
            Copy and paste the script below into your website's HTML code (preferably before the closing <code>&lt;/body&gt;</code> tag):
          </div>
          <pre style="display: block; background-color: #f1f5f9; border: 1px solid #cbd5e1; padding: 12px 14px; border-radius: 8px; font-family: monospace; font-size: 12px; white-space: pre-wrap; word-break: break-all; color: #0f172a; margin: 0; line-height: 1.5;">
&lt;script src="${baseUrl}/assets/modules/channel-web/inject.js"&gt;&lt;/script&gt;

&lt;script&gt;
  window.botpressWebChat.init({
    host: "${baseUrl}",
    botId: "${botId}"
  })
&lt;/script&gt;
          </pre>
        `,
    messenger: `
          <ol style="margin: 0; padding-left: 20px; font-size: 13px; color: #475569; line-height: 1.6;">
            <li>Messenger requires you to have a <b>Facebook App</b> and a <b>Facebook Page</b>.</li>
            <li>Visit the <b>Facebook for Developers</b> website and click <b>My Apps</b>.</li>
            <li>Under <b>Settings</b> &rarr; <b>Basic</b>, copy your <b>App Secret</b> and paste it below.</li>
            <li>Copy the Verify Token: <code>${verifyToken}</code></li>
            <li>Enable the <b>Messenger</b> integration in your App Dashboard.</li>
            <li>In <b>Messenger Api Settings</b>, generate an Access Token by linking your Page.</li>
            <li>Paste the generated <b>Access Token</b> below.</li>
            <li>Click <b>Create Bot</b> here to initialize your bot.</li>
            <li>Go back to Facebook app, configure Webhooks with callback URL: <code style="word-break: break-all;">${baseUrl}/api/v1/messaging/webhooks/${botId}/messenger</code></li>
            <li>Verify and save with the copied Verify Token.</li>
            <li>Enable <b>messages</b> and <b>messaging_postbacks</b> in Webhook Subscription.</li>
          </ol>
        `,
    whatsapp: `
             <ol style="margin: 0; padding-left: 20px; font-size: 13px; color: #475569; line-height: 1.6;">
               <li>Go to your <b>Twilio account dashboard</b> and configure your active number.</li>
               <li>Set <b>A Message Comes In</b> webhook URL to: <code style="word-break: break-all;">${baseUrl}/api/v1/messaging/webhooks/${botId}/twilio</code></li>
               <li>Copy your <b>Account SID</b> and <b>Auth Token</b> from Twilio and paste them below.</li>
               <li>In Twilio, go to <b>Messaging</b> &rarr; <b>Try it out</b> &rarr; <b>Send a Whatsapp message</b>.</li>
               <li>Under <b>Sandbox settings</b>, configure <b>A Message Comes In</b> URL with: <code style="word-break: break-all;">${baseUrl}/api/v1/messaging/webhooks/${botId}/twilio</code></li>
               <li>Save and test by sending a WhatsApp message to your Twilio number.</li>
             </ol>
           `
  }

  const getTutorialVideoSrc = () => {
    if (!selectedChannel) {
      return ''
    }
    switch (selectedChannel.id) {
      case 'webchat':
        return 'https://www.youtube.com/embed/PPgn5X7l-So'
      case 'telegram':
        return 'https://www.youtube.com/embed/XHfE8cqgdiE'
      case 'slack':
        return 'https://www.youtube.com/embed/iRU9iPO26jY'
      case 'messenger':
        return 'https://www.youtube.com/embed/CvQ-JIqU-Yk'
      case 'whatsapp':
        return 'https://www.youtube.com/embed/rP3iLSqBUBI'
      default:
        return ''
    }
  }

  const isChannelButtonDisabled = () => {
    if (!selectedChannel) {
      return true
    }
    if (selectedChannel.id === 'telegram') {
      return !(botToken.trim().length >= 20 && botToken.trim().length <= 90 && !isProcessing)
    }
    if (selectedChannel.id === 'slack') {
      return !(
        slackBotToken.trim().length >= 20 &&
        slackBotToken.trim().length <= 90 &&
        slackSigningSecret.trim().length >= 20 &&
        slackSigningSecret.trim().length <= 90 &&
        !isProcessing
      )
    }
    if (selectedChannel.id === 'messenger') {
      return !(
        messengerAccessToken.trim().length >= 50 &&
        messengerAccessToken.trim().length <= 400 &&
        messengerAppSecret.trim().length >= 20 &&
        messengerAppSecret.trim().length <= 90 &&
        !isProcessing
      )
    }
    if (selectedChannel.id === 'whatsapp') {
      return !(
        twilioAccountSid.trim().length >= 20 &&
        twilioAccountSid.trim().length <= 90 &&
        twilioAuthToken.trim().length >= 20 &&
        twilioAuthToken.trim().length <= 90 &&
        !isProcessing
      )
    }
    return false
  }

  const renderChannelCards = () => {
    const isStarter = savedSubData.subscription === 'Starter'
    const filteredChannels = isStarter ? channelList.filter(c => c.id !== 'whatsapp') : channelList

    return (
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${filteredChannels.length}, 1fr)`, gap: '12px', marginBottom: '20px' }}>
        {filteredChannels.map((c) => {
          const isSelected = selectedChannel?.id === c.id

          let iconName = 'chat'
          let iconColor = '#64748b'
          let imgUrl = ''

          switch (c.id) {
            case 'webchat':
              iconName = 'globe'
              iconColor = '#028ec0'
              break
            case 'telegram':
              imgUrl = `${process.env.PUBLIC_URL || ''}/telegram-icon.png`
              break
            case 'slack':
              imgUrl = `${process.env.PUBLIC_URL || ''}/slack-icon.png`
              break
            case 'messenger':
              imgUrl = `${process.env.PUBLIC_URL || ''}/facebook-messenger-icon.png`
              break
            case 'whatsapp':
              imgUrl = `${process.env.PUBLIC_URL || ''}/whatsapp-icon.png`
              break
          }

          return (
            <div
              key={c.id}
              onClick={() => {
                resetValues()
                setSelectedChannel(c)
              }}
              style={{
                border: isSelected ? '2px solid #028ec0' : '1px solid #cbd5e1',
                borderRadius: '10px',
                padding: '14px 8px',
                backgroundColor: isSelected ? '#f0f9ff' : '#ffffff',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                boxShadow: isSelected ? '0 4px 12px rgba(2, 142, 192, 0.15)' : 'none',
                position: 'relative'
              }}
              className={isSelected ? '' : 'template-card-hover'}
            >
              {isSelected && (
                <div style={{ position: 'absolute', top: '8px', right: '8px', color: '#028ec0' }}>
                  <Icon icon='tick-circle' iconSize={14} />
                </div>
              )}

              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: isSelected ? '#e0f2fe' : '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '8px'
                }}
              >
                {imgUrl ? (
                  <img
                    src={imgUrl}
                    alt={c.name}
                    style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                  />
                ) : (
                  <Icon icon={iconName as any} iconSize={18} color={iconColor} />
                )}
              </div>

              <div style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>
                {c.name}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={closeChannelDialog}
      title='Select a Channel'
      icon='select'
      canOutsideClickClose={false}
      className='modern-dialog'
      style={{
        width: '95%',
        maxWidth: selectedChannel ? '1100px' : '650px',
        transition: 'max-width 0.2s ease'
      }}
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <div className={Classes.DIALOG_BODY}>
          <div className='modern-form-group'>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
              {lang.tr('admin.workspace.bots.create.channel')} <span style={{ color: '#ef4444' }}>*</span>
            </label>
            {renderChannelCards()}
          </div>

          {!selectedChannel && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <Button
                onClick={closeChannelDialog}
                className='modern-btn-secondary'
                text='Back'
                style={{ padding: '8px 18px' }}
              />
            </div>
          )}

          {selectedChannel && (
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                marginTop: '16px'
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', alignItems: 'start' }}>

                {/* Left Column: Setup Tutorial Video */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <h5 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px', alignSelf: 'flex-start' }}>
                    Tutorial Video
                  </h5>
                  <iframe
                    width='100%'
                    height='260'
                    src={getTutorialVideoSrc()}
                    title='Tutorial Video'
                    style={{
                      borderRadius: '12px',
                      border: '1px solid #cbd5e1',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      backgroundColor: '#000'
                    }}
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                    allowFullScreen
                  />
                </div>

                {/* Right Column: Form Fields and Action Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <h5 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {selectedChannel.id === 'webchat' ? 'Configuration Instructions' : 'Integration Credentials'}
                  </h5>

                  <div style={{ flex: 1 }}>
                    {selectedChannel.id === 'webchat' && (
                      <div
                        className='modern-scrollbar'
                        style={{
                          maxHeight: '260px',
                          overflowY: 'auto',
                          paddingRight: '12px',
                          border: '1px solid #f1f5f9',
                          backgroundColor: '#f8fafc',
                          borderRadius: '8px',
                          padding: '14px'
                        }}
                        dangerouslySetInnerHTML={{
                          __html: channelInstructions[selectedChannel.id]
                        }}
                      />
                    )}

                    {selectedChannel.id === 'telegram' && (
                      <div className='modern-form-group'>
                        <label htmlFor='bot-token'>
                          Bot Token <span style={{ fontSize: '11px', fontWeight: 400, color: '#64748b' }}>(Must be between 20 and 90 characters.)</span>
                        </label>
                        <input
                          id='bot-token'
                          type='text'
                          className='modern-input'
                          placeholder='Enter Telegram Bot Token'
                          onChange={e => setBotToken(e.target.value)}
                          value={botToken}
                        />
                      </div>
                    )}

                    {selectedChannel.id === 'slack' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <div className='modern-form-group'>
                          <label htmlFor='slack-bot-token'>
                            Slack Bot Token <span style={{ fontSize: '11px', fontWeight: 400, color: '#64748b' }}>(Must be between 20 and 90 characters.)</span>
                          </label>
                          <input
                            id='slack-bot-token'
                            type='text'
                            className='modern-input'
                            placeholder='Enter Slack Bot Token'
                            onChange={e => setSlackBotToken(e.target.value)}
                            value={slackBotToken}
                          />
                        </div>
                        <div className='modern-form-group'>
                          <label htmlFor='slack-signing-secret'>
                            Slack Signing Secret <span style={{ fontSize: '11px', fontWeight: 400, color: '#64748b' }}>(Must be between 20 and 90 characters.)</span>
                          </label>
                          <input
                            id='slack-signing-secret'
                            type='text'
                            className='modern-input'
                            placeholder='Enter Slack Signing Secret'
                            onChange={e => setSlackSigningSecret(e.target.value)}
                            value={slackSigningSecret}
                          />
                        </div>
                      </div>
                    )}

                    {selectedChannel.id === 'messenger' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <div className='modern-form-group'>
                          <label htmlFor='messenger-access-token'>
                            Messenger Access Token <span style={{ fontSize: '11px', fontWeight: 400, color: '#64748b' }}>(Must be between 50 and 400 characters.)</span>
                          </label>
                          <input
                            id='messenger-access-token'
                            type='text'
                            className='modern-input'
                            placeholder='Enter Messenger Access Token'
                            onChange={e => setMessengerAccessToken(e.target.value)}
                            value={messengerAccessToken}
                          />
                        </div>
                        <div className='modern-form-group'>
                          <label htmlFor='messenger-app-secret'>
                            Messenger App Secret <span style={{ fontSize: '11px', fontWeight: 400, color: '#64748b' }}>(Must be between 20 and 90 characters.)</span>
                          </label>
                          <input
                            id='messenger-app-secret'
                            type='text'
                            className='modern-input'
                            placeholder='Enter Messenger App Secret'
                            onChange={e => setMessengerAppSecret(e.target.value)}
                            value={messengerAppSecret}
                          />
                        </div>
                      </div>
                    )}

                    {selectedChannel.id === 'whatsapp' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <div className='modern-form-group'>
                          <label htmlFor='twilio-account-sid'>
                            Twilio account SID <span style={{ fontSize: '11px', fontWeight: 400, color: '#64748b' }}>(Must be between 20 and 90 characters.)</span>
                          </label>
                          <input
                            id='twilio-account-sid'
                            type='text'
                            className='modern-input'
                            placeholder='Enter Twilio account SID'
                            onChange={e => setTwilioAccountSid(e.target.value)}
                            value={twilioAccountSid}
                          />
                        </div>
                        <div className='modern-form-group'>
                          <label htmlFor='twilio-auth-token'>
                            Twilio auth token <span style={{ fontSize: '11px', fontWeight: 400, color: '#64748b' }}>(Must be between 20 and 90 characters.)</span>
                          </label>
                          <input
                            id='twilio-auth-token'
                            type='text'
                            className='modern-input'
                            placeholder='Enter Twilio Auth Token'
                            onChange={e => setTwilioAuthToken(e.target.value)}
                            value={twilioAuthToken}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Error Notification inside form */}
                  {!!error && (
                    <div style={{
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: '#991b1b',
                      marginTop: '16px',
                      fontWeight: 600
                    }}>
                      {error}
                    </div>
                  )}

                  {/* Inline Form Actions */}
                  <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                    <Button
                      onClick={closeChannelDialog}
                      className='modern-btn-secondary'
                      text='Back'
                      style={{ padding: '8px 18px' }}
                    />
                    <Button
                      id='btn-modal-create-bot'
                      type='submit'
                      text={isProcessing ? lang.tr('pleaseWait') : lang.tr('admin.workspace.bots.create.create')}
                      onClick={createBot}
                      disabled={isChannelButtonDisabled()}
                      className='modern-btn-primary'
                      style={{ padding: '8px 24px', flex: 1 }}
                    />
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>
      </form>
    </Dialog>
  )
}

export default ChannelSelectionDialog
